import { useEffect, useEffectEvent, useRef } from 'react'
import { AppState, Platform } from 'react-native'
import { useTranslation } from 'react-i18next'

import {
  checkForAvailableAppUpdate,
  startAvailableAppUpdate,
} from '../services/app-update-service'
import { showAlert } from '../utils/alert'
import { captureException } from '../utils/sentry'

const MINIMUM_CHECK_INTERVAL_MS = 15 * 60 * 1000

interface MutableValue<T> {
  current: T
}

interface RunUpdateCheckOptions {
  force: boolean
  isChecking: MutableValue<boolean>
  hasPrompted: MutableValue<boolean>
  lastCheckAt: MutableValue<number>
  showIosUpdatePrompt: (currentVersion: string, latestVersion: string) => void
}

async function runUpdateCheck({
  force,
  isChecking,
  hasPrompted,
  lastCheckAt,
  showIosUpdatePrompt,
}: RunUpdateCheckOptions): Promise<void> {
  if (isChecking.current) {
    return
  }

  const now = Date.now()
  if (!force && now - lastCheckAt.current < MINIMUM_CHECK_INTERVAL_MS) {
    return
  }

  isChecking.current = true
  lastCheckAt.current = now

  try {
    const result = await checkForAvailableAppUpdate()

    if (result.status !== 'available' || hasPrompted.current) {
      return
    }

    if (result.platform === 'android') {
      if (!result.flexibleAllowed) {
        return
      }

      hasPrompted.current = true
      try {
        await startAvailableAppUpdate()
      } catch (error) {
        captureException(error, {
          feature: 'app-update',
          tags: { action: 'start_update', platform: result.platform },
        })
      }
      return
    }

    hasPrompted.current = true
    showIosUpdatePrompt(result.currentVersion, result.latestVersion)
  } catch (error) {
    captureException(error, {
      feature: 'app-update',
      tags: { action: 'check_for_available_update' },
    })
  } finally {
    isChecking.current = false
  }
}

export function useAppUpdateNotification(): void {
  const { t } = useTranslation(['common'])
  const isCheckingRef = useRef(false)
  const hasPromptedRef = useRef(false)
  const lastCheckAtRef = useRef(0)
  const showIosUpdatePrompt = useEffectEvent(
    (currentVersion: string, latestVersion: string) => {
      showAlert(
        t('common:update_available_title'),
        t('common:update_available_message', {
          currentVersion,
          latestVersion,
        }),
        [
          {
            text: t('common:later'),
            style: 'cancel',
          },
          {
            text: t('common:update_now'),
            onPress: () => {
              void startAvailableAppUpdate().catch((error) => {
                captureException(error, {
                  feature: 'app-update',
                  tags: { action: 'start_update', platform: 'ios' },
                })
              })
            },
          },
        ],
      )
    },
  )
  const checkForUpdate = useEffectEvent(async (force: boolean) => {
    await runUpdateCheck({
      force,
      isChecking: isCheckingRef,
      hasPrompted: hasPromptedRef,
      lastCheckAt: lastCheckAtRef,
      showIosUpdatePrompt,
    })
  })

  useEffect(() => {
    if (Platform.OS === 'web') {
      return
    }

    void checkForUpdate(true)

    const subscription = AppState.addEventListener('change', (nextState) => {
      if (nextState === 'active') {
        void checkForUpdate(false)
      }
    })

    return () => {
      subscription.remove()
    }
  }, [])
}
